import { kebabCase } from 'lodash/fp';
import qs from 'qs';
import { parsePhoneNumber } from 'react-phone-number-input';
import { matchPath } from 'react-router';
import Url from 'url-parse';

import { EDITOR_ROOT_PATH } from 'inquiry/lib/flowEditor';
import {
  CREATE_PERSONA_POPUP_ROUTE,
  VERIFY_WITH_PERSONA_POPUP_ROUTE,
} from 'inquiry/lib/reusablePersona';
import StorageClient, { SessionStorageKey } from 'lib/clients/StorageClient';
import ClientError from 'lib/interfaces/ClientError';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { captureException } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';
import { isAndroidIntent, isSafeRedirectUriScheme } from 'lib/utils/url';
import { focusTitleElement } from 'verify/lib/a11y';
import FlowType from 'verify/lib/interfaces/FlowType';
import InquiryParams from 'verify/lib/interfaces/InquiryParams';
import { Inquiry, RedirectQueryStringConfig } from 'verify/models/inquiry';

const DASHBOARD_PATH = '/dashboard/';

/*
 * Events sent from the widget flow to the SDK.
 * Enum values must be consistent with Persona SDKs.
 */
export enum FromWidgetEvent {
  // Lifecycle events
  Error = 'error',
  Exit = 'exit',
  Complete = 'complete',
  Fail = 'fail',
  Load = 'load',
  Ready = 'ready',
  Start = 'start',
  Success = 'success',

  // Flow related events
  DocumentCameraSelect = 'document-camera-select',
  DocumentCameraCapture = 'document-camera-capture',
  DocumentUpload = 'document-upload',
  OneTimeLinkSent = 'one-time-link-sent',
  OneTimeLinkStart = 'one-time-link-start',
  OneTimeLinkExit = 'one-time-link-exit',
  SelfieCameraSelect = 'selfie-camera-select',
  SelfieCameraCapture = 'selfie-camera-capture',
  SelfieRecordUpload = 'selfie-record-upload',

  // Misc
  /**
   * Sent when inquiry flow page changes.
   * Contains page metadata:
   * - pathname: string
   * - pageHeight: number
   */
  PageChange = 'page-change',

  /**
   * Fired when there is an issue loading the camera (permission issues, hardware issues, etc.)
   * Passes an object { error, message }.
   */
  LoadCameraFailed = 'load-camera-failed',
  /**
   * Fired when there is an issue capturing an image from the camera feed (OOM, etc.)
   * Passes an object { error, message }.
   */
  CameraCaptureFailed = 'camera-capture-failed',

  /**
   * @deprecated -- Inquiry 2.0 only
   */
  InquiryLoad = 'inquiry-load',
  /**
   * @deprecated -- Inquiry 2.0 only
   */
  CountrySelect = 'country-select',
  /**
   * @deprecated -- Inquiry 2.0 only
   */
  VerificationChange = 'verification-change',
}

/*
 * Events sent from the SDK to the widget flow.
 * Enum values must be consistent with Persona SDKs.
 */
export enum SdkEvent {
  /**
   * Fires when the SDK popup modal opens.
   */
  Open = 'open',
  Exit = 'exit',
}

interface HostedFlowQueryStringParams {
  'inquiry-id': string;
  'reference-id': string;
  status: Inquiry['attributes']['status'];
  fields: Inquiry['attributes']['fields'];
  /**
   * @deprecated alias for reference-id
   */
  subject: string;
  /**
   * @deprecated Inquiry 2.0 only object containing hardcoded field values
   */
  scopes: unknown;
}

interface WebViewFlowQueryStringParams {
  /**
   * https://docs.withpersona.com/docs/webview-flow#response
   * For legacy reasons, is only sent when FromWidgetEvent.Complete.
   * Specifically, it is undefined when the inquiry is canceled ('x' button pressed).
   */
  'inquiry-id': string | undefined;
  'reference-id': string;
  status: Inquiry['attributes']['status'];
  fields: Inquiry['attributes']['fields'];
  /**
   * @deprecated [PER-8787]
   * HACK to always send inquiry ID regardless of status without it being a
   * breaking change. Previously, presence / absence of this parameter was used
   * to determine what the inquiry status was. Now, we send status in the
   * redirect QS, and intend to always send inquiry ID.
   *
   * We currently don't have a versioning system for WebView, but should remove
   * this and always send `inquiry-id` once we do.
   */
  'persona-inquiry-id': string;
  /**
   * @deprecated alias for reference-id
   */
  subject: string;
  /**
   * @deprecated Inquiry 2.0 only object containing hardcoded field values
   */
  scopes: unknown;
}

export class Client {
  public flowType: FlowType;
  public inquiryParams: InquiryParams;
  public currentPath: string;

  constructor(pathname: string, search: string) {
    const parsedInquiryParams = qs.parse(search, {
      ignoreQueryPrefix: true,
    }) as InquiryParams;

    this.currentPath = pathname;

    if (parsedInquiryParams['is-webview']?.toLowerCase() === 'true') {
      this.flowType = FlowType.WebView;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    } else if (Object.values(FlowType).includes(parsedInquiryParams['flow-type'])) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.flowType = parsedInquiryParams['flow-type'];
    } else if (pathname.startsWith('/widget')) {
      this.flowType = FlowType.Embedded;
    } else {
      this.flowType = FlowType.Hosted;
    }

    // NB: handle backwards compatibility for parameter renames
    if (!parsedInquiryParams['template-id']) {
      parsedInquiryParams['template-id'] = parsedInquiryParams['blueprint-id'];
    }
    if (!parsedInquiryParams['reference-id']) {
      parsedInquiryParams['reference-id'] = parsedInquiryParams.subject;
    }

    // Load InquiryParams from query params or session storage, or default to {}
    const isValidGetParams =
      parsedInquiryParams['inquiry-template-id'] != null ||
      parsedInquiryParams['inquiry-template-version-id'] != null ||
      parsedInquiryParams['template-id'] != null ||
      parsedInquiryParams.code != null ||
      parsedInquiryParams['inquiry-id'] != null;

    const prenormalizedInquiryParams =
      (isValidGetParams
        ? parsedInquiryParams
        : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          JSON.parse(StorageClient.getSessionItem(SessionStorageKey.InquiryParams))) || {};

    this.inquiryParams = {
      ...prenormalizedInquiryParams,
      prefill: this.normalizePrefill(prenormalizedInquiryParams.prefill),
    };

    /*
     * NOTE:
     * We store inquiry params so that redirects in the hosted flow can be restarted.
     * There can be multiple embedded flows on the same page, so it should not use session storage.
     */
    if (this.flowType === FlowType.Hosted) {
      StorageClient.setSessionItem(
        SessionStorageKey.InquiryParams,
        JSON.stringify(this.inquiryParams),
      );
    }
  }

  /**
   * We persist the inquiry params in session storage in the constructor.
   * However, if the params include a session token, and the referenced session expires,
   * it will cause a doom loop where requests using the token 401 and then trigger a page reload.
   * The token will be properly cleared from SessionClient, but will still be hydrated from the cached inquiry params in hosted flow.
   */
  public removeSessionTokenFromCachedParams(): void {
    const sessionInquiryParams = JSON.parse(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      StorageClient.getSessionItem(SessionStorageKey.InquiryParams),
    );

    if (sessionInquiryParams) {
      delete sessionInquiryParams['session-token'];
      StorageClient.setSessionItem(
        SessionStorageKey.InquiryParams,
        JSON.stringify(sessionInquiryParams),
      );
    }
  }

  public get isEmbedded(): boolean {
    return this.flowType === FlowType.Embedded;
  }

  /**
   * DoorDash uses Embedded Flow inside WebView, so flowType will report as Embedded.
   * They also overwrite their user agent to be iOSDasherWebView and AndroidDasherWebView, among other things.
   */
  public get isWebView(): boolean {
    return this.flowType === FlowType.WebView || navigator.userAgent?.includes('WebView');
  }

  public get isHosted(): boolean {
    return this.flowType === FlowType.Hosted;
  }

  public get isInline(): boolean {
    return this.flowType === FlowType.Inline;
  }

  /**
   * Specifies widget padding with special accounting for Inline Flow.
   *
   * This is handled via HolderClient + query string params because we want
   * padding changes to ONLY be limited to inline flow, where customers may want
   * fine-grained control of positioning.
   *
   * In general we are opinionated about the layout of various screens and
   * components, and allowing full control to end users would be hard to
   * maintain while not providing much value.
   *
   * In the future, we MAY migrate this to use server styles, but that need
   * hasn't arisen yet.
   */
  public get widgetPadding(): { top: number; bottom: number; left: number; right: number } {
    const defaultPadding = {
      top: 72, // 24 + nav height of 48
      bottom: 24,
      left: 24,
      right: 24,
    };

    if (!this.isInline) return defaultPadding;

    // NOTE: Type casting is important! We can't ever directly interpolate user
    // input into Styled Components, as this risks CSS injection:
    // https://github.com/styled-components/styled-components/issues/1105
    // https://styled-components.com/docs/advanced#security
    const cast = (value: string | null | undefined, fallback: number) => {
      if (value == null) return fallback;
      if (Number.isNaN(Number(value))) return fallback;
      return Number(value);
    };

    return {
      top: cast(this.inquiryParams?.['widget-padding']?.top, defaultPadding.top),
      bottom: cast(this.inquiryParams?.['widget-padding']?.bottom, defaultPadding.bottom),
      left: cast(this.inquiryParams?.['widget-padding']?.left, defaultPadding.left),
      right: cast(this.inquiryParams?.['widget-padding']?.right, defaultPadding.right),
    };
  }

  public get isEmbeddedOrIframeIntegration(): boolean {
    // Iframe hosted integration covers both simulate and custom domains
    return (
      this.flowType === FlowType.Embedded ||
      this.flowType === FlowType.Inline ||
      window.parent !== window
    );
  }

  public get isNonIframeHostedIntegration(): boolean {
    return this.flowType === FlowType.Hosted && window.parent === window;
  }

  public get isDashboard(): boolean {
    // FIXME (lianga): Try to not rely on this method but inject the boolean into the components
    //  that require it instead
    try {
      const isDashboardPath = matchPath(window.location.pathname, {
        path: DASHBOARD_PATH,
        exact: false,
      });
      return Boolean(isDashboardPath);
    } catch (e) {
      captureException(e);
      return false;
    }
  }

  public get isDashboardFLE(): boolean {
    try {
      // FIXME (lianga): Using window doesn't accurately capture cases like in Simulate
      //  where the screen is expected to resemble that of an inquiry flow rather than that
      //  of the static previewed screen in the editor. We should try to just inject this
      //  boolean into the components that require it instead
      const isDashboardFLEPath = matchPath(window.location.pathname, {
        path: EDITOR_ROOT_PATH,
        exact: false,
      });
      return Boolean(isDashboardFLEPath);
    } catch (e) {
      captureException(e);
      return false;
    }
  }

  public get isReusablePersonaPopup(): boolean {
    const isVerifyWithPersonaPath = matchPath(this.currentPath, {
      path: VERIFY_WITH_PERSONA_POPUP_ROUTE,
      exact: false,
    });

    const isCreatePersonaPath = matchPath(this.currentPath, {
      path: CREATE_PERSONA_POPUP_ROUTE,
      exact: false,
    });

    return Boolean(isVerifyWithPersonaPath) || Boolean(isCreatePersonaPath);
  }

  public get canRequestMicrophone(): boolean {
    // If using embedded or inline flow, microphone can only be requested in versions >= 4.8.1
    // where microphone access was added to the iframe.
    // See https://github.com/persona-id/persona-verify/commit/e4d4e9a3592801da51c6448678a32aa255fac0be
    return (
      (this.flowType !== FlowType.Embedded && this.flowType !== FlowType.Inline) ||
      !this.beforeVersion('4.8.1')
    );
  }

  public get canCopyToClipboard(): boolean {
    return (
      (this.flowType !== FlowType.Embedded && this.flowType !== FlowType.Inline) ||
      !this.beforeVersion('5.1.2')
    );
  }

  public redirectToStart(routerAction?: (route: string) => void) {
    // We need to do this since window.location.assign may not refer to the
    // proper function if defined as the function default argument. The
    // reference may change when first setting up the app.
    // We also can't call it point-free for some unknown reason.
    const defaultRouterAction = (url: string) => window.location.assign(url);
    const action = routerAction ?? defaultRouterAction;
    // This is also checking for iframe hosted integrations because simulate and custom domain
    // should behave like the embedded flow on redirect to start
    if (this.isEmbeddedOrIframeIntegration) {
      action('/widget?' + qs.stringify(HolderClient.inquiryParams));
    } else if (this.flowType === FlowType.WebView || this.flowType === FlowType.Hosted) {
      action('/verify');
    } else {
      this.onError({
        status: 400,
        code: 'invalid_config',
        message: 'Unsupported flow type for redirect to start event',
      });
      throw new Error('[IGNORED] Unsupported flow type for redirect to start event');
    }
  }

  private beforeVersion(targetVersion: string): boolean {
    // First client version did not report its version.
    if (this.inquiryParams['client-version'] == null) {
      return true;
    }

    try {
      const {
        major: majorTarget,
        minor: minorTarget,
        patch: patchTarget,
      } = this.parseClientVersion(targetVersion);
      const {
        major: majorCurrent,
        minor: minorCurrent,
        patch: patchCurrent,
      } = this.parseClientVersion(this.inquiryParams['client-version']);

      if (majorCurrent < majorTarget) {
        return true;
      } else if (majorCurrent === majorTarget && minorCurrent < minorTarget) {
        return true;
      } else if (
        majorCurrent === majorTarget &&
        minorCurrent === minorTarget &&
        patchCurrent < patchTarget
      ) {
        return true;
      }
    } catch (err) {
      // Error, but allow render assuming it's the newest version
      captureException(err);
      return false;
    }

    return false;
  }

  private parseClientVersion(version: string): { major: number; minor: number; patch: number } {
    // NOTE: firefox does NOT support named capture groups
    const groups = /(\d+)\.(\d+)\.(\d+)/.exec(version);
    return {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      major: parseInt(groups[1]),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      minor: parseInt(groups[2]),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      patch: parseInt(groups[3]),
    };
  }

  private get targetOrigin() {
    // The JS SDK only sends 'iframe-origin'. Customers with special
    // integrations can also pass `'message-target-origin'` for more
    // fine-grained control for which origin `postMessage` uses.
    const originUrl =
      this.inquiryParams['message-target-origin'] ?? this.inquiryParams['iframe-origin'];

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const detectedOrigin = new Url(originUrl).origin;

    // Some apps like Ionic use URL schemes that are not recognized by
    // url-parse, such as capacitor://localhost.
    //
    // url-parse returns `'null'` for these URLs. In this case, we expect the
    // customer to have provided the right thing via `message-target-origin`
    // and just trust that.
    //
    // We reject `*` for security reasons.
    if (detectedOrigin === 'null' && originUrl !== '*') {
      return originUrl;
    } else {
      return detectedOrigin;
    }
  }

  public onError(error: ClientError) {
    const messageData = {
      containerId: this.inquiryParams['container-id'],
      templateId: this.inquiryParams['inquiry-template-id'] ?? this.inquiryParams['template-id'],
      templateVersionId: this.inquiryParams['inquiry-template-version-id'],
      name: FromWidgetEvent.Error,
      error,
    };

    window.parent.postMessage(
      // react-native-webview's onMessage requires the data variable to be a string.
      this.flowType === FlowType.WebView ? JSON.stringify(messageData) : messageData,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.targetOrigin,
    );
  }

  // TODO: refactor this to be more type safe
  // Complete/Fail MUST supply metadata: inquiryId, referenceId, scopes (v2), fields (v3); context: inquiry
  // Exit MUST supply metadata: inquiryId, sessionToken; context: inquiry
  // TODO: refactor to just pass the entire inquiry and remove the metadata field. Needless indirection.
  public onEvent(
    name: FromWidgetEvent,
    metadata: {
      [key: string]: unknown;
      inquiryId?: string;
      referenceId?: string;
      // Can be InquiryStatus or VerificationStatus via the VerificationChange event
      status?: string;
      fields?: Inquiry['attributes']['fields'];
    } = {},
    context: { [key: string]: unknown; inquiry?: Inquiry } = {},
  ) {
    if (name === FromWidgetEvent.Ready) {
      // This event is fired when the Inquiry has loaded and the spinner was just dismissed.
      // Try to focus any h1 element on load for screen readers.
      // NOTE: This is somewhat redundant with HolderClient's FromWidgetEvent.Ready handling.
      // We need both to capture initial open when a spinner may be present, and reopenings.
      if (!this.isInline) {
        // Don't steal focus for inline flows, as is it not a modal flow.
        setTimeout(focusTitleElement, 0);
      }
    }

    if (this.beforeVersion('3.1.1') && name === FromWidgetEvent.Fail) {
      // Fail was added in 3.1.1; use Exit for backwards compatibility for old SDK versions.
      name = FromWidgetEvent.Exit;
    }

    const messageData = {
      containerId: this.inquiryParams['container-id'],
      templateId: this.inquiryParams['inquiry-template-id'] ?? this.inquiryParams['template-id'],
      templateVersionId: this.inquiryParams['inquiry-template-version-id'],
      name,
      metadata,
    };

    // First, handle postMessage for ALL events
    window.parent.postMessage(
      // react-native-webview's onMessage requires the data variable to be a string.
      this.flowType === FlowType.WebView ? JSON.stringify(messageData) : messageData,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.targetOrigin,
    );

    if (import.meta.env.DEV) {
      /* eslint-disable no-console */
      console.groupCollapsed(`%c [HolderClient] ${name}`, 'color: #2a76df');
      if (metadata != null) {
        console.table(metadata);
      }
      console.groupEnd();
      /* eslint-enable no-console */
    }

    // Next, handle widget close events in a way that makes sense per-platform.
    // Embedded and Inline flows can skip; SDK handles close events
    if (this.flowType !== FlowType.Hosted && this.flowType !== FlowType.WebView) return;
    if (
      name !== FromWidgetEvent.Exit &&
      name !== FromWidgetEvent.Complete &&
      name !== FromWidgetEvent.Fail
    ) {
      return;
    }

    // Take redirectUri from querystring or from inquiry
    const inquiry = context?.inquiry;
    const redirectQueryStringConfig = inquiry?.attributes['redirect-query-string-config'];
    let redirectUri = this.inquiryParams['redirect-uri'] || inquiry?.attributes['redirect-uri'];

    if (!isSafeRedirectUriScheme(redirectUri)) {
      // Customers can detect this with the JS SDK onError
      this.onError({
        status: 400,
        code: 'invalid_config',
        message: 'Unsafe redirect. Redirects must be valid URLs.',
      });
      // Redirect customer to error page
      throw new Error('[IGNORED] Unsafe redirect. Redirects must be valid URLs.');
    }

    switch (this.flowType) {
      case FlowType.WebView: {
        redirectUri ||= 'https://personacallback';

        const qsParams: WebViewFlowQueryStringParams = {
          'inquiry-id': name === FromWidgetEvent.Complete ? metadata.inquiryId : undefined,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'persona-inquiry-id': metadata.inquiryId,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'reference-id': metadata.referenceId,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          subject: metadata.referenceId,
          // inquiry 2.0
          scopes: metadata.scopes,
          // Dynamic Flow
          status: metadata.status as Inquiry['attributes']['status'],
          fields: metadata.fields,
        };

        const processedQsParams: Partial<WebViewFlowQueryStringParams> =
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          this.applyRedirectQueryStringConfig(qsParams, redirectQueryStringConfig);

        const personaQueryString = qs.stringify(processedQsParams, { addQueryPrefix: false });
        // Append with & if redirectUri already has qs
        const redirectHasQueryString =
          redirectUri.includes('?') || decodeURIComponent(redirectUri).includes('?');
        const queryStringPrefix = redirectHasQueryString ? '&' : '?';
        const finalRedirectUri = personaQueryString
          ? redirectUri + queryStringPrefix + personaQueryString
          : redirectUri;
        window.location.assign(finalRedirectUri);
        break;
      }

      case FlowType.Hosted: {
        if (redirectUri == null) {
          if (inquiry?.attributes['next-step']?.type === UiStepType.Complete) {
            window.close();
            return;
          } else {
            this.onError({
              status: 400,
              code: 'invalid_config',
              message:
                'Close event called from hosted inquiry flow without redirect-uri; investigate template for misconfiguration',
            });
            throw new Error(
              '[IGNORED] Close event called from hosted inquiry flow without redirect-uri; investigate template for misconfiguration',
            );
          }
        } else {
          if (isAndroidIntent(redirectUri)) {
            const endPosition = redirectUri.length - 3;
            const androidRedirectUri =
              redirectUri.substring(0, endPosition) +
              'S.com.withpersona.INQUIRY_ID=' +
              metadata.inquiryId +
              ';end';
            window.location.assign(androidRedirectUri);
          } else {
            const qsParams: HostedFlowQueryStringParams = {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'inquiry-id': metadata.inquiryId,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'reference-id': metadata.referenceId,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              subject: metadata.referenceId,
              // inquiry 2.0
              scopes: metadata.scopes,
              // Dynamic Flow
              status: metadata.status as Inquiry['attributes']['status'],
              fields: metadata.fields,
            };

            const processedQsParams: Partial<HostedFlowQueryStringParams> =
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              this.applyRedirectQueryStringConfig(qsParams, redirectQueryStringConfig);

            const personaQueryString = qs.stringify(processedQsParams, { addQueryPrefix: false });
            // Append with & if redirectUri already has qs
            const redirectHasQueryString =
              redirectUri.includes('?') || decodeURIComponent(redirectUri).includes('?');
            const queryStringPrefix = redirectHasQueryString ? '&' : '?';
            const finalRedirectUri = personaQueryString
              ? redirectUri + queryStringPrefix + personaQueryString
              : redirectUri;
            window.location.assign(finalRedirectUri);
          }
        }
        break;
      }

      default:
        assertNever(this.flowType);
    }
  }

  /**
   * Note that we do not normalize email / phone in Inquiry 3.0. This is
   * because we don't know what field keys these fields will come in as.
   * These should be normalized server side, possibly with email / phone
   * field types.
   */
  private normalizePrefill(prefill: Record<PiiAttributes, string>) {
    if (!prefill) {
      return {};
    }

    let phoneNumberNormalized;
    if (prefill[PiiAttributes.PhoneNumber]) {
      phoneNumberNormalized = parsePhoneNumber(prefill[PiiAttributes.PhoneNumber], {
        defaultCountry: prefill[PiiAttributes.CountryCode] || 'US',
      });
    }

    // Query String parsers may parse "+" to space since this is part of RFC1738.
    // It's an easy mistake of not manually URI encoding the plus sign in an email address
    // (and unfortunately the browser won't do it automatically).
    //
    // We add a correction here to that should be safe. Spaces are not allowed in email
    // addresses anyway.
    let emailAddressNormalized;
    if (prefill[PiiAttributes.EmailAddress]) {
      emailAddressNormalized = prefill[PiiAttributes.EmailAddress].replace(/\s/g, '+');
    }

    return {
      ...prefill,
      [PiiAttributes.PhoneNumber]: phoneNumberNormalized ? phoneNumberNormalized.number : undefined,
      [PiiAttributes.EmailAddress]: emailAddressNormalized ?? prefill[PiiAttributes.EmailAddress],
    };
  }

  private applyRedirectQueryStringConfig<
    P extends HostedFlowQueryStringParams | WebViewFlowQueryStringParams,
  >(params: P, config: RedirectQueryStringConfig | null): Partial<P> {
    // IMPORTANT: shallow clone the two objects we are `delete`ing from to
    // avoid mutating the original argument. We especially don't want to
    // mutate `fields`, which comes from the `Inquiry` object.
    params = { ...params };
    params['fields'] = { ...params['fields'] };

    if (config != null) {
      if (!config['include-inquiry-id']) {
        delete params['inquiry-id'];

        if ('persona-inquiry-id' in params) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          delete params['persona-inquiry-id'];
        }
      }
      if (!config['include-reference-id']) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        delete params['reference-id'];
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        delete params['subject'];
      }
      if (!config['include-status']) {
        delete params['status'];
      }
      switch (config['include-fields']) {
        case 'allowlist': {
          // NOTE: we implicitly kebab-case field keys in responses, and changing this would be breaking.
          // Thus, to be safe we try to strip both the original field name and the dasherized field name.
          const allowlist = new Set(
            config['field-keys'].concat(config['field-keys'].map(kebabCase)),
          );

          // NOTE: maintain original order of field keys.
          Object.keys(params['fields']).forEach((fieldKey) => {
            if (!allowlist.has(fieldKey)) {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              delete params['fields'][fieldKey];
            }
          });
          break;
        }
        case 'blocklist': {
          // NOTE: we implicitly kebab-case field keys in responses, and changing this would be breaking.
          // Thus, to be safe we try to strip both the original field name and the dasherized field name.
          const blocklist = new Set(
            config['field-keys'].concat(config['field-keys'].map(kebabCase)),
          );

          // NOTE: maintain original order of field keys.
          Object.keys(params['fields']).forEach((fieldKey) => {
            if (blocklist.has(fieldKey)) {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              delete params['fields'][fieldKey];
            }
          });
          break;
        }
      }
    }

    return params;
  }
}

export const HolderClient = new Client(window.location.pathname, window.location.search);
